import { useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { MIN_PASSWORD_LENGTH, passwordHint, passwordLengthProblem } from "@talentkontor/domain";
import { api } from "../../data";
import { authFehler, authFehlerArt } from "../konto/auth";
import { CaptchaFeld, captchaConfig } from "../konto/Captcha";
import { InternRahmen } from "./InternRahmen";

/** Rückweg des Links aus der Mail: in den internen Bereich (eigene Sitzung), nicht nach /passwort des Kundenportals */
export const INTERN_PASSWORT_ZIEL = "/intern/passwort?neu=1";

/**
 * „Passwort vergessen“ des internen Bereichs (Inhaber 03.11.2026): Link anfordern (immer dieselbe Rückmeldung, ob es die Adresse
 * gibt oder nicht) und mit ?neu=1 aus der Mail das neue Passwort festlegen. Danach geht es zur Konsole, die den Teamzugang prüft.
 */
export function InternPasswort() {
  const [params] = useSearchParams();
  const neu = !!params.get("neu");
  return <InternRahmen title={neu ? "Neues Passwort" : "Passwort zurücksetzen"}>{neu ? <NeuesPasswort /> : <LinkAnfordern />}</InternRahmen>;
}

function LinkAnfordern() {
  const vorgabe = (useLocation().state as { email?: string } | null)?.email;
  const [email, setEmail] = useState(typeof vorgabe === "string" ? vorgabe : "");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gesendet, setGesendet] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();

  async function senden(e: FormEvent) {
    e.preventDefault();
    const mail = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail)) { setFehler("Bitte eine gültige E-Mail-Adresse eingeben."); return; }
    if (mitCaptcha && !captcha) { setFehler("Bitte zuerst die Sicherheitsprüfung lösen."); return; }
    setBusy(true);
    setFehler(null);
    try {
      await api.requestPasswordReset(mail, { captchaToken: captcha, lang: "de", redirectPath: INTERN_PASSWORT_ZIEL });
      setGesendet(true);
    } catch (err) {
      const art = authFehlerArt(err);
      // nur Sicherheitsprüfung, Grenzen und Netz melden; alles andere wie ein Erfolg (verrät keine Konten)
      if (art === "captcha" || art === "rate_limit" || art === "mail_rate_limit" || art === "network") setFehler(authFehler(err, "de"));
      else setGesendet(true);
    } finally {
      setBusy(false);
      if (mitCaptcha) { setCaptcha(null); setRunde((n) => n + 1); }
    }
  }
  return (
    <div className="co-intern-karte">
      <h1>Passwort zurücksetzen</h1>
      <p className="co-intern-unter">Für den internen Zugang. Der Link aus der Mail führt zurück in den internen Bereich.</p>
      <form onSubmit={(e) => void senden(e)} noValidate aria-busy={busy}>
        <label>
          E-Mail
          <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={runde} />}
        {fehler && <p className="tk-error" role="alert">{fehler}</p>}
        {gesendet && <p className="notice" role="status">{api.mode === "demo" ? "Demo: Es wird keine Mail verschickt." : "Wenn es zu dieser Adresse einen Zugang gibt, ist der Link unterwegs. Prüfe auch den Spam-Ordner."}</p>}
        <button className="primary" type="submit" disabled={busy}>{busy ? "Wird angefordert …" : "Link anfordern"}</button>
        <div className="co-intern-wege"><Link to="/intern/anmelden">Zurück zur Anmeldung</Link></div>
      </form>
    </div>
  );
}

function NeuesPasswort() {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState(false);
  async function speichern(e: FormEvent) {
    e.preventDefault();
    const problem = passwordLengthProblem(pw, "de") ?? (pw !== pw2 ? "Die beiden Eingaben stimmen nicht überein." : null);
    if (problem) { setFehler(problem); return; }
    setBusy(true);
    setFehler(null);
    try {
      await api.updatePassword(pw);
      setFertig(true);
    } catch (err) {
      setFehler(authFehler(err, "de"));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="co-intern-karte">
      <h1>Neues Passwort festlegen</h1>
      <p className="co-intern-unter">Für den internen Zugang.</p>
      {fertig ? (
        <div className="stack">
          <p className="notice" role="status">Dein neues Passwort ist gespeichert.</p>
          <a className="btn primary" href="/intern">Weiter zur Konsole</a>
        </div>
      ) : (
        <form onSubmit={(e) => void speichern(e)} noValidate aria-busy={busy}>
          <label>
            Neues Passwort
            <input type="password" autoComplete="new-password" required minLength={MIN_PASSWORD_LENGTH} value={pw} onChange={(e) => setPw(e.target.value)} />
          </label>
          <label>
            Passwort wiederholen
            <input type="password" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </label>
          <small className={fehler ? "tk-error" : "muted"} role={fehler ? "alert" : undefined}>{fehler ?? passwordHint("de")}</small>
          <button className="primary" type="submit" disabled={busy}>{busy ? "Wird gespeichert …" : "Passwort speichern"}</button>
          <div className="co-intern-wege"><Link to="/intern/passwort">Neuen Link anfordern</Link></div>
        </form>
      )}
    </div>
  );
}
